import { useEffect, useState } from "react"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { apiClient } from "@/lib/api"
import { Activity, Cpu, TrendingUp, Zap, BarChart3, AlertTriangle } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

interface UsageData {
  today: {
    date: string
    used: number
    limit: number
    remaining: number
    pct: number
  }
  period: {
    from: string
    to: string
    total_calls: number
    total_cost_usd: number
    total_input_tokens: number
    total_output_tokens: number
  }
  daily: Array<{ day: string; calls: number; cost_usd: number }>
  by_surface: Array<{ surface: string; calls: number; cost_usd: number }>
  by_model: Array<{ model: string; calls: number; cost_usd: number }>
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

function UsageBar({ used, limit }: { used: number; limit: number }) {
  const pct = Math.min(100, Math.round((used / limit) * 100))
  const isHigh = pct >= 80
  const isCritical = pct >= 95
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <span className="text-2xl font-semibold tabular-nums">{used}</span>
        <span className="text-sm text-muted-foreground">of {limit} calls</span>
      </div>
      <div className="h-2.5 w-full rounded-full bg-muted">
        <div
          className={cn(
            "h-full rounded-full transition-all duration-500",
            isCritical ? "bg-destructive" : isHigh ? "bg-warning" : "bg-primary"
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{pct}% used</span>
        <span>{limit - used} remaining</span>
      </div>
    </div>
  )
}

function SparkLine({ data, maxHeight = 40 }: { data: number[]; maxHeight?: number }) {
  if (!data.length) return null
  const max = Math.max(...data, 1)
  const barWidth = Math.max(4, Math.min(12, Math.floor(280 / data.length)))
  const gap = 2
  const svgWidth = data.length * (barWidth + gap)
  return (
    <svg width={svgWidth} height={maxHeight} className="mt-2">
      {data.map((v, i) => {
        const h = Math.max(1, (v / max) * maxHeight)
        return (
          <rect
            key={i}
            x={i * (barWidth + gap)}
            y={maxHeight - h}
            width={barWidth}
            height={h}
            rx={1}
            className="fill-primary/60"
          />
        )
      })}
    </svg>
  )
}

function SurfaceLabel({ surface }: { surface: string }) {
  const labels: Record<string, string> = {
    display: "Display Messages",
    guidebook: "Guidebook",
    review_intel: "Review Intelligence",
    research: "Guide Research",
    listing_audit: "Listing Audit",
    listing_generate: "Content Generator",
    listing_content_gen: "Content Generator",
    photo_analysis: "Photo Analysis",
    guidebook_highlights: "Guidebook Highlights",
    admin_populate_ai: "Admin Populate AI",
    host_strategy_summary: "Strategy Summary",
    performance_insights: "Performance Insights",
    forecast_interpretation: "Forecast Interpretation",
    overview_intelligence: "Overview Intelligence",
    review_intelligence: "Review Intelligence",
    strategy: "Strategy Overview",
    fallback_populate: "Fallback Populate",
    market_digest: "Market Digest",
    forecast: "Revenue Forecast",
    research_enrich: "Guide Research",
  }
  return <>{labels[surface] || surface}</>
}

export function UsagePage() {
  const { propertyId } = usePropertyContext()
  const [data, setData] = useState<UsageData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    if (!propertyId) return
    setLoading(true)
    setError("")
    apiClient.getUsageDashboard(propertyId).then((res) => {
      if (!res.ok) {
        setError(res.error || "Failed to load usage data.")
        return
      }
      setData(res as unknown as { ok: true } & UsageData)
    }).catch((err) => {
      setError(err instanceof Error ? err.message : "Something went wrong.")
    }).finally(() => setLoading(false))
  }, [propertyId])

  if (loading) {
    return (
      <div className="space-y-6 p-1">
        <div className="space-y-1">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-72" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-1">
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-center">
          <AlertTriangle className="mx-auto h-8 w-8 text-destructive mb-3" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    )
  }

  if (!data) return null

  const { today, period, daily, by_surface, by_model } = data

  return (
    <div className="space-y-6 p-1">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold tracking-tight">AI Usage</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          LLM call consumption and cost breakdown for the last 30 days
        </p>
      </div>

      {/* Today's quota */}
      <Card className="border-border/50 bg-card/80 backdrop-blur-sm shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            <Zap className="h-4 w-4 text-primary" />
            Today&apos;s Quota
          </CardTitle>
        </CardHeader>
        <CardContent>
          <UsageBar used={today.used} limit={today.limit} />
        </CardContent>
      </Card>

      {/* KPI strip */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border-border/50 bg-card/80 backdrop-blur-sm shadow-sm">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
              <Activity className="h-3.5 w-3.5" />
              Total Calls
            </div>
            <p className="text-2xl font-semibold tabular-nums">{period.total_calls.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-1">Last 30 days</p>
          </CardContent>
        </Card>

        <Card className="border-border/50 bg-card/80 backdrop-blur-sm shadow-sm">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
              <TrendingUp className="h-3.5 w-3.5" />
              Total Cost
            </div>
            <p className="text-2xl font-semibold tabular-nums">${period.total_cost_usd.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-1">Last 30 days</p>
          </CardContent>
        </Card>

        <Card className="border-border/50 bg-card/80 backdrop-blur-sm shadow-sm">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
              <Cpu className="h-3.5 w-3.5" />
              Input Tokens
            </div>
            <p className="text-2xl font-semibold tabular-nums">{formatTokens(period.total_input_tokens)}</p>
            <p className="text-xs text-muted-foreground mt-1">Last 30 days</p>
          </CardContent>
        </Card>

        <Card className="border-border/50 bg-card/80 backdrop-blur-sm shadow-sm">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
              <Cpu className="h-3.5 w-3.5" />
              Output Tokens
            </div>
            <p className="text-2xl font-semibold tabular-nums">{formatTokens(period.total_output_tokens)}</p>
            <p className="text-xs text-muted-foreground mt-1">Last 30 days</p>
          </CardContent>
        </Card>
      </div>

      {/* Daily trend */}
      <Card className="border-border/50 bg-card/80 backdrop-blur-sm shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            <BarChart3 className="h-4 w-4 text-primary" />
            Daily Calls (30 days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {daily.length > 0 ? (
            <SparkLine data={daily.map((d) => d.calls)} maxHeight={48} />
          ) : (
            <p className="text-sm text-muted-foreground">No data yet</p>
          )}
        </CardContent>
      </Card>

      {/* Breakdowns */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* By surface */}
        <Card className="border-border/50 bg-card/80 backdrop-blur-sm shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              By Feature
            </CardTitle>
          </CardHeader>
          <CardContent>
            {by_surface.length > 0 ? (
              <div className="space-y-2.5">
                {by_surface.map((s) => (
                  <div key={s.surface} className="flex items-center justify-between text-sm">
                    <span className="truncate mr-3">
                      <SurfaceLabel surface={s.surface} />
                    </span>
                    <div className="flex items-center gap-3 shrink-0">
                      <Badge variant="secondary" className="tabular-nums text-xs">
                        {s.calls}
                      </Badge>
                      <span className="text-xs text-muted-foreground tabular-nums w-16 text-right">
                        ${s.cost_usd.toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No data yet</p>
            )}
          </CardContent>
        </Card>

        {/* By model */}
        <Card className="border-border/50 bg-card/80 backdrop-blur-sm shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              By Model
            </CardTitle>
          </CardHeader>
          <CardContent>
            {by_model.length > 0 ? (
              <div className="space-y-2.5">
                {by_model.map((m) => (
                  <div key={m.model} className="flex items-center justify-between text-sm">
                    <span className="truncate mr-3 font-mono text-xs">{m.model}</span>
                    <div className="flex items-center gap-3 shrink-0">
                      <Badge variant="secondary" className="tabular-nums text-xs">
                        {m.calls}
                      </Badge>
                      <span className="text-xs text-muted-foreground tabular-nums w-16 text-right">
                        ${m.cost_usd.toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No data yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
